import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, PackageX, Plus, Minus, Boxes, History, Upload } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import CsvImportModal from "@/components/app/CsvImportModal";
import { btnOutline, inputCls, pageSub, pageTitle } from "@/components/app/ui";

const level = (p) => (p.stock <= 0 ? "out" : p.stock <= (p.min_stock || 5) ? "low" : "ok");

// Per-row stock adjuster with inline validation.
function AdjustCell({ product, onAdjust }) {
  const [val, setVal] = useState("");
  const [err, setErr] = useState("");
  const run = async (delta) => {
    const n = val === "" ? 1 : Number(val);
    if (!Number.isInteger(n) || n < 1) return setErr("Enter a whole number ≥ 1.");
    if (n > 100000) return setErr("Too large.");
    if (delta < 0 && n > product.stock) return setErr(`Only ${product.stock} in stock.`);
    setErr("");
    if (await onAdjust(product, delta * n)) setVal("");
  };
  return (
    <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1.5">
        <button onClick={() => run(-1)} data-testid={`inventory-dec-${product.row_id || product.id}`} aria-label="Decrease stock" className="w-7 h-7 rounded-md bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center"><Minus size={13} /></button>
        <input type="number" min="1" value={val} onChange={(e) => { setVal(e.target.value); setErr(""); }} placeholder="1" aria-invalid={err ? "true" : "false"}
          data-testid={`inventory-input-${product.row_id || product.id}`} className={`${inputCls} !h-7 !w-16 text-center ${err ? "!border-red-500" : ""}`} />
        <button onClick={() => run(1)} data-testid={`inventory-inc-${product.row_id || product.id}`} aria-label="Increase stock" className="w-7 h-7 rounded-md bg-green-50 text-green-600 hover:bg-green-100 flex items-center justify-center"><Plus size={13} /></button>
      </div>
      {err && <span role="alert" className="text-[11px] text-red-600 mt-1">{err}</span>}
    </div>
  );
}

export default function Inventory() {
  const [data, setData] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const load = useCallback(() => api.get("/vendor/inventory").then(({ data }) => setData(data)), []);
  useEffect(() => { load(); }, [load]);

  const onAdjust = useCallback(async (p, change) => {
    try {
      await api.post("/vendor/inventory/adjust", { product_id: p.id, variant_id: p.variant_id || null, change, reason: "manual", note: "Manual adjustment" });
      toast.success(`${p.name}${p.variant_label ? ` (${p.variant_label})` : ""}: stock ${change > 0 ? "increased" : "decreased"} by ${Math.abs(change)} (now ${p.stock + change}).`);
      load();
      return true;
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); return false; }
  }, [load]);

  const stockCols = useMemo(() => [
    { key: "name", header: "Product", cell: (p) => <span className="font-semibold text-neutral-900">{p.name}{p.variant_label && <span className="ml-1.5 text-[12px] font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5">{p.variant_label}</span>}</span> },
    { key: "sku", header: "SKU", hideBelow: "md", accessor: (p) => p.sku || "", cell: (p) => <span className="text-slate-500 font-mono text-[12px]">{p.sku || "—"}</span> },
    { key: "min_stock", header: "Alert at", hideBelow: "lg", align: "center", accessor: (p) => p.min_stock || 5 },
    { key: "stock", header: "Stock", align: "center", accessor: (p) => p.stock, cell: (p) => {
      const l = level(p);
      return <span className={`font-bold ${l === "out" ? "text-red-600" : l === "low" ? "text-orange-600" : "text-green-600"}`}>{p.stock}</span>;
    } },
    { key: "level", header: "Level", accessor: level, cell: (p) => {
      const l = level(p);
      const cls = l === "out" ? "bg-red-50 text-red-700 border-red-200" : l === "low" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-green-50 text-green-700 border-green-200";
      return <span className={`inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium ${cls}`}>{l === "out" ? "Out of stock" : l === "low" ? "Low stock" : "In stock"}</span>;
    } },
    { key: "adjust", header: "Adjust", sortable: false, align: "center", cell: (p) => <AdjustCell product={p} onAdjust={onAdjust} /> },
  ], [onAdjust]);

  const ledgerCols = useMemo(() => [
    { key: "product_name", header: "Product" },
    { key: "change", header: "Change", align: "center", cell: (l) => <span className={`font-bold ${l.change >= 0 ? "text-green-600" : "text-red-600"}`}>{l.change >= 0 ? "+" : ""}{l.change}</span> },
    { key: "reason", header: "Reason", cell: (l) => <span className="capitalize text-slate-600">{(l.reason || "").replace(/_/g, " ")}</span> },
    { key: "note", header: "Note", hideBelow: "md", cell: (l) => <span className="text-slate-600">{l.note || "—"}</span> },
    { key: "created_at", header: "Date", accessor: (l) => l.created_at || "", cell: (l) => <span className="text-slate-600 font-mono text-[12px] whitespace-nowrap">{(l.created_at || "").slice(0, 19).replace("T", " ")}</span> },
  ], []);

  if (!data) return <div className="text-slate-500 text-[13px]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="vendor-inventory">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Inventory</h1>
          <p className={pageSub}>Adjust stock and track every movement.</p>
        </div>
        <button onClick={() => setImportOpen(true)} data-testid="inventory-import-btn" className={btnOutline}><Upload size={15} /> Bulk adjust (CSV)</button>
      </div>
      <CsvImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} title="Bulk stock adjustment from CSV"
        subtitle="Set or change stock for many products and variants in one go." templateUrl="/vendor/inventory/import-template" templateLabel="Download current stock sheet" fileName="inventory-adjustment.csv"
        importUrl="/vendor/inventory/import" columns="sku, name, variant, current_stock, new_stock, change, note"
        hint="Download the stock sheet, then fill new_stock (set to this count) or change (+10 / -3) on the rows you want to adjust and leave the rest blank. Rows are matched by SKU, and every change is recorded in the ledger."
        summary={(r) => [{ count: r.updated, label: "to adjust" }, ...(r.unchanged ? [{ count: r.unchanged, label: "unchanged" }] : []), ...(r.failed ? [{ count: r.failed, label: "with errors", tone: "bad" }] : [])]} />

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-gradient-to-br from-orange-50 to-white border border-orange-100 px-4 py-3 flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center"><AlertTriangle size={17} /></span>
          <div><div className="text-[22px] font-bold leading-tight">{data.low_stock.length}</div><div className="text-[12px] text-slate-500">Low stock</div></div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-red-50 to-white border border-red-100 px-4 py-3 flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center"><PackageX size={17} /></span>
          <div><div className="text-[22px] font-bold leading-tight">{data.out_of_stock.length}</div><div className="text-[12px] text-slate-500">Out of stock</div></div>
        </div>
      </div>

      <section className="space-y-2">
        <h2 className="text-[14px] font-bold text-neutral-900">Stock levels</h2>
        <DataTable testId="inventory-table" exportName="stock-levels" columns={stockCols} data={data.products} rowKey={(p) => p.row_id || p.id} rowTestId={(p) => `inventory-row-${p.row_id || p.id}`}
          searchKeys={["name", "sku"]} searchPlaceholder="Search product or SKU…" filters={[{ key: "level", label: "All levels", accessor: level, options: [{ value: "ok", label: "In stock" }, { value: "low", label: "Low stock" }, { value: "out", label: "Out of stock" }] }]}
          defaultSort={{ key: "stock", dir: "asc" }} emptyIcon={Boxes} emptyTitle="No products" emptyText="Add products to manage their stock." />
      </section>

      <section className="space-y-2">
        <h2 className="text-[14px] font-bold text-neutral-900">Inventory ledger</h2>
        <DataTable testId="ledger-table" exportName="inventory-ledger" columns={ledgerCols} data={data.ledger} rowKey={(l) => l.id}
          searchKeys={["product_name", "note", "reason"]} searchPlaceholder="Search ledger…" filters={[{ key: "reason", label: "All reasons", accessor: (l) => l.reason }]}
          dateField={{ label: "Date", accessor: (l) => l.created_at }} defaultSort={{ key: "created_at", dir: "desc" }}
          emptyIcon={History} emptyTitle="No movements yet" emptyText="Every stock change is recorded here." />
      </section>
    </div>
  );
}
